/* eslint-disable id-length */
/* eslint-disable max-depth */
import { v4 as uuid } from 'uuid';
import * as R from 'ramda';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';

import { segmentDirections } from '@constants/component-configs';

import { createTemporalId } from '@utils/data-detail-utils';

export const getBatch = fileId => JSON.parse(sessionStorage.getItem(fileId));

// Format the row_nums for display, finding sequences.
export const formatRowNums = rowNums => {
  if (!Array.isArray(rowNums)) {
    return rowNums.toString();
  }

  // Find continuous sequences.
  const sequences = [];
  let currentSeq = [rowNums[0]];

  for (let i = 1; i < rowNums.length; i++) {  // eslint-disable-line no-plusplus
    if (rowNums[i] === currentSeq[currentSeq.length - 1] + 1) {
      currentSeq.push(rowNums[i]);
    } else {
      sequences.push(currentSeq);
      currentSeq = [rowNums[i]];
    }
  }
  sequences.push(currentSeq);

  // Format sequences
  return sequences.map(seq =>
    seq.length > 1 ? `${seq[0]}-${seq[seq.length - 1]}` : seq[0].toString()
  ).join(', ');
};

// Returns a set with the unique line numbers in the errors object.
export const getUniqueErrors = errors => new Set(errors.map(error => error.line_number));

// Convert the data returned by the batch upload API into a list of rows to display in the UI.
const processBatchRows = fileContent => {
  const { rows } = fileContent;
  const errors = getUniqueErrors(fileContent.errors);

  // Convert into an array of rows:
  const entries = Object.entries(rows).map(([key, value]) => ({
    ...value,
    display_row_nums: formatRowNums(value.row_nums),
    hasErrors: value.row_nums.some(number => errors.has(number)),
    id: key
  }));

  // Sort the array based on the first row number found in 'row_nums'.
  return entries.sort((a, b) => {
    // Ensure row_nums exists and has at least one element:
    const rowA = a.row_nums?.[0] ?? Infinity;
    const rowB = b.row_nums?.[0] ?? Infinity;
    return rowA - rowB;
  });
};

export const saveBatch = (entity, file, fileContent) => {
  const fileId = uuid();
  const rows = processBatchRows(fileContent);
  const data = { entity, errors: fileContent.errors, file, rows };
  sessionStorage.setItem(fileId, JSON.stringify(data));
  return { fileId, rows };
};

// The name field on the CSV comes as "[entity_type_name]_name", so we have
// to convert it:
const convertName = (data, typeName) => {
  const nameField = `${typeName}_name`;
  if (data[nameField]) {
    return { name: data[nameField] };
  }
  return {};
};

// Process category value and return category IDs or null
const processCategoryValue = (value, type, categories) => {
  if (type.is_multiple) {
    const values = String(value).split(',')
      .map(v => v.trim());
    const categoryIds = values.reduce((acc, val) => {
      const category = Object.values(categories).find(item => item.type === type.id && item.name.toLowerCase() === val.toLowerCase());
      if (category) {
        acc.push(category.id);
      }
      return acc;
    }, []);

    return categoryIds.length > 0 ? categoryIds : null;
  }

  const category = Object.values(categories).find(item => item.type === type.id && item.name.toLowerCase() === value.toLowerCase());
  return category ? category.id : null;
};

// Convert location fields from CSV into a segment:
const convertLocation = (locations, segmentImpactTypes) => {
  if (!locations) {
    return {};
  }

  const segments = locations.map(data => {
    const {
      on_street,
      from_address,
      from_street,
      to_address,
      to_street,
      is_alley,
      direction: original_direction,
      impact_type: original_impact_type,
      // With other we pass any custom segment property a city might have
      // (like Soundtransit's from_station, to_station, etc).
      ...other
    } = data;

    let direction = original_direction;
    let impact_type = original_impact_type;

    // Convert direction value if it exists:
    if (direction) {
      if (direction === segmentDirections.one.label) {
        direction = segmentDirections.one.value;
      } else
        if (direction === segmentDirections.both.label) {
          direction = segmentDirections.both.value;
        } else
          if (direction === 'Unknown') {
            direction = 0;
          }
    }

    // Convert impact_type:
    if (impact_type) {
      const impactType = R.find(R.propEq(impact_type, 'name'))(Object.values(segmentImpactTypes));
      if (impactType) {
        impact_type = impactType.id;
      }
    }

    let display_to = null;
    let display_from = null;

    // If theres address information fill the display_xxxx fields
    // so the input boxes on the location card gets filled.
    if (!isEmpty(on_street)) {
      if (!isEmpty(to_street)) {
        display_to = `${to_street} & ${on_street}`;
      } else
        if (!isEmpty(to_address)) {
          display_to = `${to_address} ${on_street}`;
        }

      if (!isEmpty(from_street)) {
        display_from = `${from_street} & ${on_street}`;
      } else
        if (!isEmpty(from_address)) {
          display_from = `${from_address} ${on_street}`;
        }
    }

    const segment = {
      id: createTemporalId(),
      on_street,
      from_address,
      from_street,
      to_address,
      to_street,
      shape: null,
      display_to,
      display_from,
      direction,
      impact_type,
      is_alley,
      ...other
    };

    return segment;
  });

  return { segments };
};

export const convertCSVDataForContext = (data, typeName, categoryTypes, categories, segmentImpactTypes) => {
  if (data?.data) {
    const result = {
      ...data.data,
      ...convertLocation(data.locations, segmentImpactTypes),
      ...convertName(data.data, typeName)
    };

    // Split date/time fields similar to data-detail-reducer logic
    if (result.start_date && !result.start_time && !result.all_day) {
      result.start_time = moment(result.start_date);
    }
    if (result.end_date && !result.end_time && !result.all_day) {
      result.end_time = moment(result.end_date);
    }

    // Prefix category fields with '|' and convert values.
    if (categoryTypes) {
      Object.values(categoryTypes).forEach(type => {
        const key = type.label.toLowerCase().replace(/ /g, '_')
          .replace(/\//g, '_')
          .replace(/\?/g, '');

        let categoryResult = null;

        if (result[key]) {
          const value = result[key];
          categoryResult = processCategoryValue(value, type, categories);
        }

        // Always set the category field, even if no valid categories found
        result[`|${type.name}`] = categoryResult;
        delete result[key];
      });
    }

    if (!result.segments) {
      result.segments = [];
    }
    return result;
  }
  return {};
};

export const convertCSVErrorsForContext = (data, errors) => {
  const converted = {};
  errors.forEach(({ column, details, line_number }) => {
    if (column === 'segments' && details === 'This field is required.') {
      // Skip the segments error if it's the one about the field being required.
      // Since we always create a new instance (an empty one if there's no data),
      // when editing a row (this follows the same behavior as the entity edit page).
      return;
    }
    if (column === null &&
      details === 'An address must have either an address or a cross_street not both'
    ) {
      return;
    }
    if (data?.row_nums?.includes(line_number)) {
      if (!converted[column]) {
        converted[column] = [];
      }
      converted[column].push(details);
    }
  });

  return converted;
};

export const convertBatchToFile = (batch, categoryTypes, categories) => {
  const { entityType, fileName, rows } = batch;

  // Filter out skipped rows
  const unskippedRows = Object.values(rows).filter(row => !row.skipped);

  // Get all unique keys from all rows to create a complete header
  const headerSet = new Set();
  let hasAnySegmentData = false;
  unskippedRows.forEach(row => {
    Object.keys(row.data).forEach(key => {
      headerSet.add(key);
    });
    // Check if there is segment data that is not empty
    if (row.data.segments && row.data.segments.length > 0) {
      const segment = row.data.segments[0];
      if (segment.on_street || segment.from_address || segment.from_street || segment.to_address || segment.to_street) {
        hasAnySegmentData = true;
      }
    }
  });

  // Dynamically determine segment-specific fields by collecting all fields present in segment objects
  // Exclude fields that shouldn't be sent to the backend (like id and shape)
  const excludedSegmentFields = new Set(['id', 'shape']);
  const segmentFields = new Set();
  unskippedRows.forEach(row => {
    const segments = row.data.segments || [];
    segments.forEach(segment => {
      Object.keys(segment).forEach(field => {
        if (!excludedSegmentFields.has(field)) {
          segmentFields.add(field);
        }
      });
    });
  });

  // Add specific segment fields we want to include in the CSV export
  const segmentFieldsToInclude = ['title', 'description'];
  segmentFieldsToInclude.forEach(field => {
    if (segmentFields.has(field)) {
      headerSet.add(field);
    }
  });

  const addressFields = ['on_street', 'from_address', 'from_street', 'to_address', 'to_street'];
  if (hasAnySegmentData) {
    addressFields.forEach(field => headerSet.add(field));
  }

  let header = Array.from(headerSet);

  // Skip the 'type' (entity type) attribute, we are already passing
  // it in the API call.
  header = header.filter(key => key !== 'type');

  // Skip segments, the batch upload backend only understands the addresses
  // not the geometries.
  header = header.filter(key => key !== 'segments');

  // The categories are sent as individual fields, so remove the 'categories' entry.
  header = header.filter(key => key !== 'categories');

  // Skip start_time and end_time as they are included in start_date and end_date
  header = header.filter(key => key !== 'start_time' && key !== 'end_time');

  // Skip deletedSegmentIds - frontend-only field for tracking deleted segments
  header = header.filter(key => key !== 'deletedSegmentIds');

  // If there's a 'contact' header, replace it with specific contact fields.
  if (header.includes('contact')) {
    const contactIndex = header.indexOf('contact');
    header.splice(contactIndex, 1, 'contact email', 'contact name', 'contact phone');
  }

  // Generate CSV rows - one row per segment for entities with multiple segments
  const csvRows = [];
  unskippedRows.forEach(row => {
    const segments = row.data.segments || [];
    const numSegments = Math.max(segments.length, 1); // At least one row even if no segments

    // eslint-disable-next-line no-plusplus
    for (let segmentIndex = 0; segmentIndex < numSegments; segmentIndex++) {
      const csvRow = header.map(key => {
        let value;

        // Determine if this is the first row of the entity or a subsequent location row
        const isFirstRow = segmentIndex === 0;
        const isEntityField = !segmentFields.has(key) && key !== 'external_id';

        // For first row: include all entity data (with segment data from first segment)
        // For subsequent rows: only include external_id and segment-specific fields
        if (isFirstRow || !isEntityField) {
          if (isEntityField) {
            // Entity-level fields (only included in first row)
            value = row.data[key];

            if (key === 'contact email') {
              value = row.data.contact?.email;
            } else
              if (key === 'contact name') {
                value = row.data.contact?.name;
              } else
                if (key === 'contact phone') {
                  value = row.data.contact?.phone;
                } else
                  if ((key === 'start_date' || key === 'end_date') && value) {
                    if (moment(value).isValid()) {
                      if (row.data.all_day) {
                        value = moment(value).format('YYYY-MM-DD');
                      } else {
                        value = moment(value).format(); // ISO 8601 format
                      }
                    }
                  } else
                    if (key.startsWith('|')) {
                      // If it's a category, find the category name as replace the value with the name.
                      const categoryTypeName = key.substring(1);
                      const categoryType = Object.keys(categoryTypes).find(item => categoryTypes[item].name === categoryTypeName);
                      if (categoryType) {
                        if (categoryTypes[categoryType].is_multiple) {
                          // If a multiple category is filled, value will be null for the rows in which the multiple category wasn't filled.
                          if (value) {
                            const valuesMult = [];
                            Object.values(categories).forEach(category => {
                              if (value.includes(category.id)) {
                                valuesMult.push(category.name);
                              }
                            });
                            value = valuesMult.join(',');
                          }
                        } else {
                          const category = Object.values(categories).find(item => item.id === value);
                          if (category) {
                            value = category.name;
                          }
                        }
                      }
                    }
          } else {
            // Segment-specific fields or external_id - use data from the specific segment
            const segment = segments[segmentIndex];
            if (segmentFields.has(key)) {
              value = segment?.[key];
            } else if (key === 'external_id') {
              // External ID comes from entity data, not segment data
              value = row.data[key];
            }
          }
        }
        // For subsequent rows of multi-segment entities, entity fields are left empty

        // Set the empty string if it's null or undefined, but not when zero or false.
        value = value ?? '';

        // Escape commas, quotes, and newlines in values
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      });

      csvRows.push(csvRow.join(','));
    }
  });

  // Strip leading "|" character on category fields:
  header = header.map(key => key.startsWith('|') ? key.substring(1) : key);

  const csvContent = [header.join(','), ...csvRows].join('\n');

  // Create a new File object
  return new File([csvContent], fileName, { type: entityType });
};
